/**
 * Chat storage — cleaning up SEVERAL members at once (#758 part 2).
 *
 * Two steps, and the split is the safety:
 *
 *   STEP 1 ticking only chooses WHO. Nothing is decided here, which is
 *          why the list can be scanned quickly and why a mis-tick costs
 *          nothing.
 *   STEP 2 one setting for everyone, and a PER-MEMBER preview of what
 *          each person loses before anything runs.
 *
 * ── Why the preview is per member and not a total ─────────────────────
 *
 * A total hides the two cases that change an admin's mind: the person who
 * loses far more than expected, and the person for whom this does nothing
 * at all. Both are visible here as their own line ("nothing to clear",
 * "unchanged"), so a selection that would not help is obvious before it
 * is confirmed rather than afterwards.
 *
 * ── One booking per member, and they are independent ──────────────────
 *
 * Each member gets their own booking, because each is cancellable from
 * their own row and each is notified individually. A member who already
 * has one booked is REFUSED by the database, and this reports that as a
 * skip rather than failing the whole batch — the other bookings are still
 * the right thing to do.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from '@papan/i18n'
import { storageApi, type ChatStorageCleanupResult, type MemberChatStorageRow } from '../../client/storage'
import { formatBytes } from '../budget/bits'
import { readPreview, readBooking, worthBooking } from './cleanupVerdict'
import { nameFromEmail } from '@papan/core-ui/ui'
import { pinnedBatch } from '@papan/api-client'


/**
 * One line of the dialog: a ticked member, or the "Uploader unknown" row
 * (Season 2026-10-22: it is ticked like a member and cleaned up with
 * them). Its key can never be an email, and nobody is notified about it.
 */
interface Target {
  key: string
  label: string
  totalBytes: number
  limitBytes: number | null
  request: { target: 'member'; email: string } | { target: 'unattributed' }
}
const UNATTRIBUTED_KEY = ' unattributed'

interface PerMember {
  /** `Target.key` — the member's email, or `UNATTRIBUTED_KEY`. */
  email: string
  result: ChatStorageCleanupResult | null
  failed: boolean
}

const DELAY_UNITS = [
  { key: 'hours', minutes: 60 },
  { key: 'days', minutes: 60 * 24 },
  { key: 'weeks', minutes: 60 * 24 * 7 },
] as const

/** Under a day is not much notice. Not refused — an admin may have a good
 *  reason — but said out loud, because the days are the whole point of
 *  booking rather than deleting, and a number typed in a hurry is exactly
 *  how somebody loses work they would have saved. */
const SHORT_NOTICE_MINUTES = 24 * 60

/** The window, typed rather than chosen (Season 2026-10-17 — the agreed
 *  artboard had a dropdown; a free field covers the ones it did not list). */
const MIN_IDLE_DAYS = 1
const MAX_IDLE_DAYS = 3650
function wholeIdleDays(v: string): number | null {
  const t = v.trim()
  if (t === '' || !/^\d+$/.test(t)) return null
  const n = Number(t)
  return Number.isSafeInteger(n) && n >= MIN_IDLE_DAYS && n <= MAX_IDLE_DAYS ? n : null
}

export function CleanUpMembersDialog({
  members, unattributedBytes, onClose, onBooked,
}: {
  /** The ticked members, in the order the list showed them. */
  members: MemberChatStorageRow[]
  /** Present ⇒ the "Uploader unknown" row was ticked too; its bytes. */
  unattributedBytes?: number
  onClose: () => void
  onBooked: () => void
}) {
  const { t } = useTranslation('core-ui')
  const targets: Target[] = [
    ...members.map((m) => ({
      key: m.email, label: nameFromEmail(m.email), totalBytes: m.totalBytes, limitBytes: m.limitBytes,
      request: { target: 'member' as const, email: m.email },
    })),
    ...(unattributedBytes === undefined ? [] : [{
      key: UNATTRIBUTED_KEY, label: t('settings.storage.chatStorageUnattributed'), totalBytes: unattributedBytes, limitBytes: null,
      request: { target: 'unattributed' as const },
    }]),
  ]
  const [idleText, setIdleText] = useState('30')
  const [includeStarred, setIncludeStarred] = useState(false)
  const [delayAmount, setDelayAmount] = useState('7')
  const [delayUnit, setDelayUnit] = useState<(typeof DELAY_UNITS)[number]['key']>('days')
  const idleDays = wholeIdleDays(idleText)
  /** The last answer, and the window it answered FOR (see `rows`). */
  const [answered, setAnswered] = useState<{ forKey: string; rows: PerMember[] } | null>(null)
  const [previewing, setPreviewing] = useState(false)
  const [busy, setBusy] = useState(false)
  const [done, setDone] = useState<{ booked: number; unattributedBooked: boolean; clashed: number; failed: number; skipped: number; incomplete: number; partial: number; truncated: number; stopping: number; startsNow: boolean; conversations: number } | null>(null)
  const [confirmingNow, setConfirmingNow] = useState(false)

  const unit = DELAY_UNITS.find((u) => u.key === delayUnit)!
  const amount = /^\d+$/.test(delayAmount.trim()) ? Number(delayAmount.trim()) : null
  const runInMinutes = amount === null ? null : amount * unit.minutes
  const validDelay = runInMinutes !== null && runInMinutes >= 0 && runInMinutes <= 365 * 24 * 60
  const runsNow = runInMinutes === 0
  /** Short, but not nothing — a different warning from "runs now". */
  const shortNotice = runInMinutes !== null && runInMinutes > 0 && runInMinutes < SHORT_NOTICE_MINUTES

  const key = `${idleDays}:${includeStarred}:${targets.map((m) => m.key).join(',')}`
  /**
   * ⚠️ ONLY WHILE IT ANSWERS FOR WHAT IS ON SCREEN — the single dialog's
   * `forKey` rule, which this dialog lacked (⑥ review #7). For the 300 ms
   * debounce after the window changed, the previous window's rows were
   * still here and Confirm was armed on them: a press in that gap booked
   * (or skipped) members from an answer to a different question.
   */
  const rows = answered && answered.forKey === key ? answered.rows : null
  /** Asked, or about to be: no answer for this window yet. */
  const waiting = idleDays !== null && rows === null
  const seq = useRef(0)
  const previewAll = useCallback(async () => {
    // A cleared window also retires any preview still in flight, or its
    // answer for the OLD window could land after this one.
    if (idleDays === null) { ++seq.current; setPreviewing(false); return }
    const forKey = key
    const mine = ++seq.current
    setPreviewing(true)
    // SEQUENTIAL, not Promise.all. Each preview walks every workspace's
    // database; firing ten at once would have one admin's dialog scanning
    // the organisation ten times over simultaneously.
    const out: PerMember[] = []
    for (const m of targets) {
      try {
        const r = await storageApi.cleanUpChatStorage({
          ...m.request, idleDays, includeStarred,
        })
        out.push({ email: m.key, result: r, failed: false })
      } catch {
        out.push({ email: m.key, result: null, failed: true })
      }
      if (seq.current !== mine) return
    }
    if (seq.current === mine) { setAnswered({ forKey, rows: out }); setPreviewing(false) }
    // `key` names the targets; the array itself is rebuilt every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key])

  useEffect(() => {
    const id = setTimeout(() => { void previewAll() }, 300)
    return () => clearTimeout(id)
    // `key` carries every input the preview depends on.
  }, [key, previewAll])

  const totalBytes = (rows ?? []).reduce((n, r) => n + (r.result?.bytes ?? 0), 0)
  /**
   * Confirm is armed while ANY target is worth booking (`worthBooking`, the
   * one rule both dialogs use — D32). That includes an answer we could not
   * get: a preview that threw, or one that came back empty because a
   * workspace database could not be opened.
   *
   * ⚠️ CONFIRM HAS TO STAY AVAILABLE FOR THEM. It was disabled on
   * `totalConversations === 0` alone, so an organisation whose only
   * unreadable workspace belonged to the one member with idle
   * conversations showed "nothing to clear" for everybody and a dead
   * button — and `book`'s own rule for unproven zeros, which exists to
   * book exactly these people, could never run. A zero we MEASURED is a
   * reason not to act; a zero we merely OBSERVED is not.
   */
  const anyWorthBooking = targets.some((m) => {
    const row = rows?.find((r) => r.email === m.key)
    return row ? worthBooking(readPreview(row.result, row.failed)) : false
  })

  const book = async () => {
    if (!validDelay || idleDays === null) return
    setBusy(true)
    // #1316 review round 2 (owner, 2026-10-28): the bookings the admin confirmed FINISH as the identity that confirmed
    // them — every booking goes (and is audited) as it, never as the next session; nothing is shown once that ended
    const batch = pinnedBatch()
    let booked = 0
    let clashed = 0
    let failed = 0
    let skipped = 0
    let incomplete = 0
    let partial = 0
    /** Their last cleanup is still stopping — nothing booked; try again. */
    let stopping = 0
    /** Lists that hit what one booking can freeze — more is left. */
    let truncated = 0
    /** What the bookings will work through, as frozen — the "all 9,480"
     *  of the started line (D35). */
    let conversations = 0
    /** Booked: members are counted in `booked`; the uploader-unknown row
     *  gets its own line, since "Booked for 2 members" must not count it. */
    let unattributedBooked = false
    for (const m of targets) {
      // ⚠️ BOOK THE ROWS THAT HAVE SOMETHING, NOT THE TICKED LIST.
      //
      // The preview above tells the admin, per person, "nothing to clear
      // → unchanged". This loop used to book every ticked member anyway,
      // so those people got a bell saying some of their idle
      // conversations will be cleared — about a screen that had just
      // told the admin they would lose nothing. And because the criteria
      // are re-evaluated on the run date, any of their conversations
      // that went quiet during the notice week WOULD then be deleted:
      // an outcome the admin was shown as "unchanged".
      //
      // A preview that FAILED is not a zero. We cannot prove that member
      // has nothing, and silently dropping them would leave the admin
      // believing they are covered — so they are still booked.
      const preview = rows?.find((r) => r.email === m.key)
      // ⚠️ A ZERO FROM AN INCOMPLETE SCAN IS NOT A ZERO, and the HTTP
      // call succeeding does not make it one. `preview.failed` catches a
      // request that threw; a scan that could not open some workspace's
      // database answers 200 with `conversations: 0` and a
      // `workspacesSkipped`. Skipping on that dropped the member here,
      // client-side, so the server's own guard never got the chance to
      // fire — the loop's stated rule ("a failure is not a zero") was
      // true of the comment and false of the code below it.
      // D32: ONE rule for "nothing to book" — a measured zero (see
      // `worthBooking`). No answer yet is not a zero, so it is booked.
      const reading = preview ? readPreview(preview.result, preview.failed) : null
      if (reading && !worthBooking(reading)) {
        skipped++
        continue
      }
      try {
        const r = await batch.run(() => storageApi.cleanUpChatStorage({
          ...m.request, idleDays: idleDays!, includeStarred, apply: true, runInMinutes: runInMinutes!,
        }))
        if (m.request.target === 'unattributed') unattributedBooked = true
        else booked++
        // Members only: the "Started for N members … all M" line is about
        // them, and the uploader-unknown row has its own line.
        if (m.request.target === 'member') conversations += r.conversations ?? 0
        // Booked, but off an answer that was missing a workspace — the
        // list is a subset and the admin has to know to come back.
        // ⚠️ FROM THE BOOKING'S OWN ANSWER, not the preview's (issues-to-
        // file #1): the booking froze its list with its own scan.
        const notes = readBooking(r)
        if (notes.partial) partial++
        if (notes.truncated) truncated++
      } catch (e) {
        // A CLASH IS NOT A FAILURE, and the first cut counted them the
        // same — both branches incremented `clashed`, so the API going
        // down after member 2 of 8 rendered "6 already had cleanups
        // booked and were skipped". The admin would read that as "they
        // are covered" and walk away with six people unbooked.
        //
        // A clash is benign and self-explaining: that member already has
        // one. (It used to say "cancellable from their row" — true of a
        // BOOKING, false of one that is already running, which is the very
        // distinction the lines below were added for. Round 13, finding 9.)
        // Anything else means nothing was booked for them and somebody has
        // to act.
        const code = (e as { code?: string })?.code
        // `CLEANUP_ALREADY_RUNNING` counts here too: it is the same benign
        // "that member already has one" outcome, just one that cannot be
        // cancelled yet. The summary line's wording ("already had a cleanup
        // booked and were skipped") is true of both, so this stays one
        // counter rather than a second line nobody asked for. A member
        // whose cleanup the worker is carrying out right now — since D35,
        // any "start now" for its first minute or more — is how it is
        // reached (round 12, finding 2).
        if (code === 'CLEANUP_ALREADY_SCHEDULED' || code === 'CLEANUP_ALREADY_RUNNING') clashed++
        // ⚠️ NOT A CLASH. Their last cleanup was STOPPED and is finishing
        // its batch: nothing will run for them, so "already had a cleanup
        // booked" would read as covered — the failure the comment above
        // warns about. Its own line says to try again shortly.
        else if (code === 'CLEANUP_STOPPING') stopping++
        // NOT A FAILURE EITHER. The server refuses an empty booking, and
        // this loop deliberately still tries a member whose PREVIEW
        // failed — we cannot prove they have nothing, so we ask. When the
        // answer comes back "nothing to do", that is the same benign
        // outcome as the zeros skipped above, not something an admin has
        // to come back and redo. Counting it under `failed` told them one
        // member "could not be booked, try again" for ever, about
        // somebody with nothing to clear.
        else if (code === 'CLEANUP_NOTHING_TO_DO') skipped++
        // ⚠️ NOT BENIGN. The server refused because it could not read
        // everything, so nothing is booked and nobody is notified — and
        // unlike "nothing to clear", that is worth coming back for. Its
        // own counter rather than `failed`, because the cure is "try
        // again in a minute" (somebody's agent turn is finishing), not
        // "something is broken".
        else if (code === 'CLEANUP_INCOMPLETE_SCAN') incomplete++
        else failed++
      }
    }
    setBusy(false)
    if (batch.ended()) return
    setConfirmingNow(false)
    setDone({ booked, unattributedBooked, clashed, failed, skipped, incomplete, partial, truncated, stopping, startsNow: runsNow, conversations })
    onBooked()
  }

  const runsAt = runInMinutes === null ? null : new Date(Date.now() + runInMinutes * 60 * 1000)

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50 ws-anim-fade-in" onClick={onClose} />
      <div className="relative z-10 max-w-xl w-full max-h-[85vh] flex flex-col ws-glass-modal ws-anim-modal-in">

        <div className="px-6 pt-5 pb-3.5 themed-border border-b">
          <h3 className="text-[17px] font-semibold themed-text">
            {unattributedBytes === undefined
              ? t('settings.storage.cleanupMembersTitle', { count: members.length })
              : t('settings.storage.cleanupMembersTitlePlusUnattributed', { count: members.length })}
          </h3>
          <p className="text-xs themed-text-muted mt-1 leading-relaxed">
            {t('settings.storage.cleanupSubtitle')}
          </p>
        </div>

        <div className="px-6 py-4 overflow-y-auto min-h-0 flex-1 space-y-4">
          {done ? (
            <div className="space-y-2">
              {/* A started line about NOBODY ("Started for 0 members … all 0")
                  is noise: when every member clashed or had nothing, the
                  lines below say why. The booked-for-later headline keeps
                  its old rule. */}
              {(done.startsNow ? done.booked > 0 : (done.booked > 0 || !done.unattributedBooked)) && (
                <p className="text-sm themed-text">
                  {/* STARTED, NOT CLEARED (D35): nothing is deleted yet.
                      The worker clears every list in the background, and
                      Cleanup activity is where it is watched. */}
                  {done.startsNow
                    ? t('settings.storage.cleanupStartedForAll', { count: done.booked, total: done.conversations })
                    : t('settings.storage.cleanupBookedForAll', { count: done.booked })}
                </p>
              )}
              {done.unattributedBooked && (
                <p className="text-sm themed-text">
                  {t(done.startsNow ? 'settings.storage.cleanupUnattributedStarted' : 'settings.storage.cleanupUnattributedBooked')}
                </p>
              )}
              {done.startsNow && (done.booked > 0 || done.unattributedBooked) && (
                <p className="text-[13px] themed-text-muted">{t('settings.storage.cleanupStartedWatch')}</p>
              )}
              {done.skipped > 0 && (
                // Said out loud, because the completion line counts only
                // the people actually booked: without this, an admin who
                // ticked eight and sees "booked for 2" has no way to tell
                // whether the other six were skipped on purpose or lost.
                <p className="text-[13px] themed-text-muted">
                  {t('settings.storage.cleanupSomeNothingToClear', { count: done.skipped })}
                </p>
              )}
              {done.clashed > 0 && (
                <p className="text-[13px] text-amber-500">
                  {t('settings.storage.cleanupSomeClashed', { count: done.clashed })}
                </p>
              )}
              {done.partial > 0 && (
                // The single dialog gained this line and the bulk one
                // did not — so eight thin bookings reported as "Booked
                // for 8 members" with nothing saying the scan had been
                // incomplete for any of them.
                <p className="text-[13px] text-amber-500">
                  {t('settings.storage.cleanupSomePartial', { count: done.partial })}
                </p>
              )}
              {done.stopping > 0 && (
                <p className="text-[13px] text-amber-500">
                  {t('settings.storage.cleanupSomeStopping', { count: done.stopping })}
                </p>
              )}
              {done.truncated > 0 && (
                <p className="text-[13px] text-amber-500">
                  {t('settings.storage.cleanupSomeTruncated', { count: done.truncated })}
                </p>
              )}
              {done.incomplete > 0 && (
                <p className="text-[13px] text-amber-500">
                  {t('settings.storage.cleanupSomeIncomplete', { count: done.incomplete })}
                </p>
              )}
              {done.failed > 0 && (
                // Its OWN line, in the louder colour. Nothing was booked
                // for these people and somebody has to do it again.
                <p className="text-[13px] text-rose-500">
                  {t('settings.storage.cleanupSomeFailed', { count: done.failed })}
                </p>
              )}
            </div>
          ) : (
            <>
              <div className="flex items-center gap-3 flex-wrap">
                <label className="text-sm font-semibold themed-text" htmlFor="cleanup-many-idle">
                  {t('settings.storage.cleanupIdleLabel')}
                </label>
                <input
                  id="cleanup-many-idle"
                  type="text"
                  inputMode="numeric"
                  value={idleText}
                  onChange={(e) => setIdleText(e.target.value)}
                  className="w-20 text-sm px-2.5 py-1.5 rounded-lg themed-border border themed-bg-tertiary themed-text"
                />
                <span className="text-sm themed-text-muted">{t('settings.storage.cleanupUnit_days')}</span>
                {idleDays === null && (
                  <span className="text-[12px] text-rose-400">
                    {t('settings.storage.cleanupIdleInvalid', { min: MIN_IDLE_DAYS, max: MAX_IDLE_DAYS })}
                  </span>
                )}
              </div>

              <div className="flex items-start gap-2.5">
                <input
                  id="cleanup-many-starred"
                  type="checkbox"
                  checked={includeStarred}
                  onChange={(e) => setIncludeStarred(e.target.checked)}
                  className="mt-0.5"
                />
                <label htmlFor="cleanup-many-starred" className="text-[13px] themed-text">
                  {t('settings.storage.cleanupIncludeStarredAny')}
                  <span className="block text-xs themed-text-muted mt-0.5">
                    {t('settings.storage.cleanupStarredHint')}
                  </span>
                </label>
              </div>

              <div className="rounded-xl themed-border border overflow-hidden">
                {targets.map((m) => {
                  const row = rows?.find((r) => r.email === m.key)
                  // DRAWN FROM THE SHARED READING (D32), as the single
                  // dialog's box is.
                  const reading = row ? readPreview(row.result, row.failed) : null
                  const n = reading && (reading.kind === 'some' || reading.kind === 'partial') ? reading.conversations : 0
                  const freed = reading && (reading.kind === 'some' || reading.kind === 'partial') ? reading.bytes : 0
                  const after = Math.max(0, m.totalBytes - freed)
                  return (
                    <div key={m.key} className="flex items-center gap-2.5 px-3.5 py-2 text-[13px] themed-border border-b last:border-b-0">
                      <span className={`flex-1 min-w-0 truncate themed-text${m.request.target === 'unattributed' ? ' italic' : ''}`}>{m.label}</span>
                      {reading === null ? (
                        // No answer for this window yet — never "nothing to
                        // clear", which is what an empty answer used to read as.
                        previewing || waiting ? <span className="themed-text-muted">{t('settings.storage.cleanupChecking')}</span> : null
                      ) : reading.kind === 'failed' ? (
                        <span className="text-rose-500">{t('settings.storage.cleanupPreviewFailed')}</span>
                      ) : reading.kind === 'partial' ? (
                        /* FOUND SOMETHING, BUT NOT EVERYTHING. The count
                           is real and incomplete at the same time, and
                           rendering it bare let an admin book a thin
                           list believing it was the whole one. */
                        <>
                          <span className="themed-text-muted">
                            {t('settings.storage.cleanupConversations', { count: n })} · {formatBytes(freed)}
                          </span>
                          <span className="text-amber-500">{t('settings.storage.cleanupPartial')}</span>
                        </>
                      ) : reading.kind === 'unproven' ? (
                        /* ⚠️ A ZERO NOBODY COULD PROVE. Rendering this as
                           "nothing to clear" told the admin a member was
                           fine when the only honest answer was "could not
                           look". They ARE still booked (see `book`). */
                        <span className="text-amber-500">{t('settings.storage.cleanupCouldNotCheck')}</span>
                      ) : reading.kind === 'nothing' ? (
                        <>
                          {/* The line that stops a pointless confirmation:
                              this person would lose nothing, so ticking
                              them achieved nothing. */}
                          <span className="themed-text-muted italic">{t('settings.storage.cleanupNothingToClear')}</span>
                          <span className="themed-text-muted">→ {t('settings.storage.cleanupUnchanged')}</span>
                        </>
                      ) : (
                        <>
                          <span className="themed-text-muted">
                            {t('settings.storage.cleanupConversations', { count: n })} · {formatBytes(freed)}
                          </span>
                          {/* A FLOOR, NOT A FORECAST. `freed` is the raw
                              size of the attachments in those
                              conversations; the member's own figure
                              counts a repeated file once and ignores one
                              that is also in the drive. So the real
                              figure can only be this or HIGHER, never
                              lower — "at best" is the one word that
                              makes the arrow true. */}
                          <span className="themed-text font-semibold tabular-nums">
                            → {t('settings.storage.cleanupAtBest', { size: formatBytes(after) })}
                            {m.limitBytes !== null ? ` / ${formatBytes(m.limitBytes)}` : ''}
                          </span>
                        </>
                      )}
                    </div>
                  )
                })}
                <div className="px-3.5 py-2 text-[13px] font-semibold themed-text themed-bg-tertiary">
                  {t('settings.storage.cleanupReclaimsTotal', { size: formatBytes(totalBytes) })}
                </div>
              </div>

              <div>
                <p className="text-sm font-semibold themed-text mb-2">{t('settings.storage.cleanupWhenLabel')}</p>
                <div className="flex items-center gap-2 flex-wrap">
                  <input
                    type="text"
                    inputMode="numeric"
                    value={delayAmount}
                    onChange={(e) => setDelayAmount(e.target.value)}
                    aria-label={t('settings.storage.cleanupWhenLabel')}
                    className="w-20 text-sm px-2.5 py-1.5 rounded-lg themed-border border themed-bg-tertiary themed-text"
                  />
                  <select
                    value={delayUnit}
                    onChange={(e) => setDelayUnit(e.target.value as typeof delayUnit)}
                    aria-label={t('settings.storage.cleanupWhenUnit')}
                    className="text-sm px-2.5 py-1.5 rounded-lg themed-border border themed-bg-tertiary themed-text"
                  >
                    {DELAY_UNITS.map((u) => (
                      <option key={u.key} value={u.key}>{t(`settings.storage.cleanupUnit_${u.key}`)}</option>
                    ))}
                  </select>
                  <span className="text-sm themed-text-muted">{t('settings.storage.cleanupFromNow')}</span>
                </div>
                {!validDelay && (
                  <p className="text-[12px] text-rose-400 mt-1.5">{t('settings.storage.cleanupWhenInvalid')}</p>
                )}
              </div>

              {validDelay && (
                <div className={`rounded-xl px-4 py-3 ${runsNow ? 'border border-rose-500/40 bg-rose-500/10' : 'themed-bg-tertiary'}`}>
                  {runsNow ? (
                    <>
                      <p className="text-[13px] text-rose-400 font-semibold">{t('settings.storage.cleanupRunsNow')}</p>
                      <p className="text-xs themed-text-muted mt-0.5">{t('settings.storage.cleanupRunsNowHint')}</p>
                    </>
                  ) : (
                    <>
                      <p className="text-[13px] themed-text">
                        {t('settings.storage.cleanupRunsAt', { when: formatWhen(runsAt?.toISOString()) })}
                        {' · '}
                        {t('settings.storage.cleanupAllNotified', { count: members.length })}
                      </p>
                      {/* UNDER A DAY — see the sibling dialog. */}
                      {shortNotice && (
                        <p className="text-xs text-amber-500 mt-1">{t('settings.storage.cleanupShortNoticeMany')}</p>
                      )}
                    </>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        <div className="px-6 py-3.5 themed-border border-t flex items-center justify-end gap-2.5">
          <button type="button" onClick={onClose} className="text-[13px] px-4 py-2 rounded-lg themed-border border themed-text">
            {t(done ? 'common:close' : 'common:cancel')}
          </button>
          {!done && (
            confirmingNow ? (
              <>
                <span className="text-[13px] text-rose-400 mr-auto">{t('settings.storage.cleanupConfirmNowAsk')}</span>
                <button
                  type="button"
                  onClick={() => { void book() }}
                  disabled={busy}
                  className="text-[13px] px-4 py-2 rounded-lg bg-rose-700 text-white font-semibold disabled:opacity-60"
                >
                  {busy ? t('settings.storage.cleanupWorking') : t('settings.storage.cleanupConfirmNowYes')}
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={() => { if (runsNow) setConfirmingNow(true); else void book() }}
                disabled={busy || previewing || waiting || !validDelay || idleDays === null || !anyWorthBooking}
                className={`text-[13px] px-4 py-2 rounded-lg text-white font-semibold disabled:opacity-60 ${runsNow ? 'bg-rose-700' : 'bg-rose-600'}`}
              >
                {busy
                  ? t('settings.storage.cleanupWorking')
                  : runsNow
                    ? t('settings.storage.cleanupClearMembersNow', { count: members.length })
                    : t('settings.storage.cleanupConfirm')}
              </button>
            )
          )}
        </div>
      </div>
    </div>
  )
}

function formatWhen(iso?: string): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleString(undefined, {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  })
}
